// Session storage for HLPT Co-Pilot.
//
// Stored shape (localStorage key `hlpt_session_v2`):
//   {
//     employee: {
//       id, name, team, baseId,            // primary identity
//       bases: [{ baseId, uid, baseName }], // all bases this user accesses
//       email, welcomeEnabled?
//     },
//     activeBaseId: 'app...'   // which of `bases` is currently selected
//   }
//
// `bases` is at least 1 entry. For shared-roster users (e.g. Guest
// Support + Agency Support) it's 2+. The AppShell shows a toggle when
// length > 1 so the user can switch roster while working — only ONE
// base is active at a time, which keeps API reads/writes simple.

import { setApiBase } from './api.js';

const STORAGE_KEY = 'hlpt_session_v2';

function pickActiveBase(employee, requestedBaseId) {
  const bases = Array.isArray(employee?.bases) ? employee.bases : [];
  if (!bases.length) return null;
  if (requestedBaseId) {
    const match = bases.find(b => b.baseId === requestedBaseId);
    if (match) return match;
  }
  return bases[0]; // primary
}

export function loadSession() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.employee?.id) return null;
    if (!Array.isArray(parsed.employee.bases)) return null;
    // HR-only employees (Phil — Head of Fulfillment, etc.) have bases:[]
    // — no team-base task tracker, but a valid Co-Pilot session for the
    // Org tab. setApiBase(null) is the right default; downstream code
    // either skips the X-HLPT-Base header (org endpoints) or guards on
    // bases.length (Time Tracker tab).
    const active = pickActiveBase(parsed.employee, parsed.activeBaseId);
    setApiBase(active);
    return { ...parsed, activeBaseId: active?.baseId || parsed.employee.baseId || null };
  } catch {
    return null;
  }
}

export function saveSession(session) {
  if (!session?.employee?.id || !Array.isArray(session?.employee?.bases)) {
    clearSession();
    return;
  }
  const active = pickActiveBase(session.employee, session.activeBaseId);
  const toStore = { ...session, activeBaseId: active?.baseId || session.employee.baseId || null };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore));
  setApiBase(active);
}

/**
 * Switch the active base without invalidating the session. Returns the
 * new session shape so the caller can update React state.
 */
export function setActiveBase(session, baseId) {
  const active = pickActiveBase(session?.employee, baseId);
  if (!active) return session;
  const next = { ...session, activeBaseId: active.baseId };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  setApiBase(active);
  return next;
}

export function clearSession() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
  try { localStorage.removeItem('hlpt_session_v1'); } catch {}
  setApiBase(null);
}
